import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, LabelList } from 'recharts';
import SalesLayout from '../../components/SalesLayout';
import PageLoader from '../../components/PageLoader';
import AuthService from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';
import { canShareStudentForm } from '../../utils/studentForm';

function resolveFinancialYearInfo() {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth(); // 0 = Jan, 3 = Apr

    const startYear = monthIndex >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    const shortLabel = `FY ${startYear}-${String(endYear).slice(-2)}`;
    const periodLabel = `Apr ${startYear} to Mar ${endYear}`;

    return { startYear, endYear, shortLabel, periodLabel };
}

function buildFinancialYearMonths(fy) {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Apr (4) to Dec (12) of startYear
    for (let m = 4; m <= 12; m += 1) {
        const monthKey = `${fy.startYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.startYear}`;
        months.push({ monthKey, label });
    }

    // Jan (1) to Mar (3) of endYear
    for (let m = 1; m <= 3; m += 1) {
        const monthKey = `${fy.endYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.endYear}`;
        months.push({ monthKey, label });
    }

    return months;
}

function formatAmount(value) {
    const amount = Number.isFinite(value) ? (value ?? 0) : 0;
    return `Rs ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(amount))}`;
}

function resolveAchievementTone(rate) {
    if (rate >= 110) {
        return 'text-emerald-600';
    }

    if (rate >= 90) {
        return 'text-emerald-500';
    }

    if (rate >= 70) {
        return 'text-amber-500';
    }

    return 'text-rose-600';
}

function resolveRingColor(rate) {
    if (rate >= 90) return '#10b981';
    if (rate >= 70) return '#f59e0b';
    return '#f43f5e';
}

function useIsMobile() {
    const query = '(max-width: 639px)';
    const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
    useEffect(() => {
        const mq = window.matchMedia(query);
        const onChange = (event) => setMobile(event.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return mobile;
}

function greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
}

const STATUS_STYLES = [
    { bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 ring-sky-200' },
    { bar: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 ring-violet-200' },
    { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 ring-amber-200' },
    { bar: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 ring-rose-200' },
    { bar: 'bg-teal-500', chip: 'bg-teal-50 text-teal-700 ring-teal-200' },
];

const iconProps = { className: 'h-5 w-5', fill: 'none', stroke: 'currentColor', strokeWidth: 2, viewBox: '0 0 24 24' };
const ICONS = {
    inquiries: (
        <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    ),
    followups: (
        <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
    ),
    fresh: (
        <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
    ),
    target: (
        <svg {...iconProps}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
    ),
};

const rise = keyframes`
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
`;

const growX = keyframes`
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
`;

const Reveal = styled.div`
    animation: ${rise} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${(p) => p.$delay || 0}ms;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Segment = styled.div`
    transform-origin: left center;
    animation: ${growX} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${(p) => p.$delay || 0}ms;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Counts from 0 up to `value` when it first appears (and again whenever the value changes).
function CountUp({ value, format = (n) => String(Math.round(n)), duration = 900 }) {
    const [display, setDisplay] = useState(0);
    const frame = useRef(0);

    useEffect(() => {
        const target = Number.isFinite(value) ? value : 0;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            setDisplay(target);
            return undefined;
        }
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - (1 - t) ** 3;
            setDisplay(target * eased);
            if (t < 1) frame.current = requestAnimationFrame(tick);
        };
        frame.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame.current);
    }, [value, duration]);

    return <>{format(display)}</>;
}

function StatCard({ label, value, format, hint, tone, icon, delay }) {
    return (
        <Reveal $delay={delay} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-lg sm:p-5">
            <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10 transition group-hover:scale-125 ${tone.glow}`} />
            <div className="relative flex items-start justify-between gap-2 sm:gap-3">
                <div className="min-w-0">
                    <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">{label}</p>
                    <p className="mt-1 truncate text-xl sm:mt-2 sm:text-3xl font-bold tracking-tight text-slate-900"><CountUp value={value} format={format} /></p>
                    {hint ? <p className="mt-0.5 truncate text-[11px] text-slate-500 sm:mt-1 sm:text-xs">{hint}</p> : null}
                </div>
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white shadow-md sm:h-11 sm:w-11 sm:rounded-xl ${tone.icon}`}>
                    {icon}
                </div>
            </div>
        </Reveal>
    );
}

function Card({ title, subtitle, aside, children, delay }) {
    return (
        <Reveal as="section" $delay={delay} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex items-start justify-between gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-sm font-semibold text-slate-900 sm:text-base">{title}</h2>
                    {subtitle ? <p className="mt-0.5 hidden text-sm text-slate-500 sm:block">{subtitle}</p> : null}
                </div>
                {aside}
            </div>
            {children}
        </Reveal>
    );
}

function Empty({ children }) {
    return (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 sm:mt-6 sm:py-12 text-center text-sm text-slate-500">
            {children}
        </div>
    );
}

function ChartTooltip({ active, payload, label, names }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 text-sm shadow-xl backdrop-blur">
            <p className="mb-1 text-xs font-semibold text-slate-500">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} className="flex items-center gap-2 text-slate-800">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                    <span className="text-slate-500">{names[entry.dataKey] || entry.dataKey}</span>
                    <span className="font-semibold">{entry.dataKey === 'count' ? entry.value : formatAmount(entry.value)}</span>
                </p>
            ))}
        </div>
    );
}

function DailyChart({ data, gradientId, from, to, name }) {
    const mobile = useIsMobile();
    return (
        <div className="mt-3 h-44 sm:mt-6 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={from} />
                            <stop offset="100%" stopColor={to} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="label" stroke="#64748b" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} interval={mobile ? 'preserveStartEnd' : 0} minTickGap={mobile ? 12 : 5} />
                    <YAxis stroke="#64748b" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} width={mobile ? 28 : 40} />
                    <Tooltip cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }} content={<ChartTooltip names={{ count: name }} />} />
                    <Bar dataKey="count" fill={`url(#${gradientId})`} radius={[8, 8, 0, 0]} maxBarSize={28} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function AchievementRing({ rate }) {
    const [shown, setShown] = useState(0);
    useEffect(() => {
        const id = setTimeout(() => setShown(rate), 250);
        return () => clearTimeout(id);
    }, [rate]);
    const radius = 54;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.min(100, Math.max(0, shown));
    return (
        <div className="relative h-24 w-24 shrink-0 sm:h-36 sm:w-36">
            <svg viewBox="0 0 128 128" className="h-full w-full -rotate-90">
                <circle cx="64" cy="64" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" />
                <circle
                    cx="64"
                    cy="64"
                    r={radius}
                    fill="none"
                    stroke={resolveRingColor(rate)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - clamped / 100)}
                    style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(0.22, 1, 0.36, 1)' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-xl font-bold sm:text-3xl ${resolveAchievementTone(rate)}`}><CountUp value={rate} format={(n) => `${Math.round(n)}%`} duration={1100} /></span>
                <span className="text-[9px] font-medium uppercase tracking-wide text-slate-500 sm:text-[11px]">achieved</span>
            </div>
        </div>
    );
}

export default function Dashboard({ pageTitle, section }) {
    const [statusSummary, setStatusSummary] = useState([]);
    const [registrationData, setRegistrationData] = useState([]);
    const [followupData, setFollowupData] = useState([]);
    const [inquiryData, setInquiryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get('/admin/sales-dashboard');
                if (!response?.data || cancelled) return;

                const data = response.data;
                setStatusSummary(data.inquiryStatusSummary ?? []);
                setRegistrationData(data.monthlyRegistrations ?? []);
                setFollowupData(data.dailyFollowups ?? []);
                setInquiryData(data.dailyInquiries ?? []);
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load sales dashboard data.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, []);

    const totalInquiries = statusSummary.reduce(
        (sum, item) => sum + (Number.isFinite(item.count) ? item.count : 0),
        0,
    );

    const financialYearInfo = useMemo(() => resolveFinancialYearInfo(), []);
    const totalFollowups = followupData.reduce((sum, item) => sum + item.count, 0);
    const totalNewInquiries = inquiryData.reduce((sum, item) => sum + item.count, 0);
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const firstName = String(AuthService.getUser()?.superAdmin?.staff?.name || '').trim().split(/\s+/)[0];

    const chartData = useMemo(() => {
        if (!financialYearInfo) {
            return registrationData.map((row) => ({
                label: row.label,
                targetAmount: row.targetAmount ?? 0,
                achievedAmount: row.registeredAmount ?? 0,
                balanceAmount: Math.max(0, (row.targetAmount ?? 0) - (row.registeredAmount ?? 0)),
                achievementRate: (row.targetAmount ?? 0) > 0 ? ((row.registeredAmount ?? 0) / (row.targetAmount ?? 0)) * 100 : 0,
            }));
        }

        const fyMonths = buildFinancialYearMonths(financialYearInfo);
        const byKey = {};
        for (const row of registrationData) {
            byKey[row.monthKey] = row;
        }

        return fyMonths.map(({ monthKey, label }) => {
            const row = byKey[monthKey];
            const target = row?.targetAmount ?? 0;
            const achieved = row?.registeredAmount ?? 0;
            const balance = Math.max(0, target - achieved);
            const rate = target > 0 ? (achieved / target) * 100 : 0;

            return {
                label,
                targetAmount: target,
                achievedAmount: achieved,
                balanceAmount: balance,
                achievementRate: rate,
            };
        });
    }, [registrationData, financialYearInfo]);

    const totals = chartData.reduce(
        (acc, row) => {
            acc.target += row.targetAmount ?? 0;
            acc.achieved += row.achievedAmount ?? 0;
            return acc;
        },
        { target: 0, achieved: 0 },
    );
    const pendingTotal = Math.max(0, totals.target - totals.achieved);
    const overallRate = totals.target > 0 ? (totals.achieved / totals.target) * 100 : 0;
    const overallRateRounded = Math.round(overallRate);

    const resolvedTitle = pageTitle || 'Sales dashboard';

    return (
        <SalesLayout title={resolvedTitle} currentSection={section}>
            {loading ? (
                <PageLoader label="Loading sales dashboard…" />
            ) : (
            <div className="space-y-3 sm:space-y-6">
                {error ? (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error}</div>
                ) : null}

                <Reveal as="section" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-4 text-white shadow-lg sm:rounded-3xl sm:p-8">
                    <div className="absolute -right-10 -top-10 h-48 w-48 animate-pulse rounded-full bg-white/10" />
                    <div className="absolute -bottom-16 right-24 h-40 w-40 animate-pulse rounded-full bg-white/10" style={{ animationDelay: '0.8s' }} />
                    <div className="relative flex flex-col gap-3 sm:gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="hidden sm:block">
                            <p className="text-xs font-medium text-white/80 sm:text-sm">{todayLabel}</p>
                            <h1 className="mt-0.5 text-lg sm:mt-1 sm:text-2xl font-bold tracking-tight sm:text-3xl">
                                {greeting()}{firstName ? `, ${firstName}` : ''} 👋
                            </h1>
                            <p className="mt-2 hidden max-w-xl text-sm text-white/80 sm:block">
                                Here is how your sales team is doing in {currentMonth}.
                            </p>
                            {financialYearInfo ? (
                                <span className="mt-2 inline-flex max-w-full items-center truncate rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold backdrop-blur sm:mt-4 sm:text-xs">
                                    {financialYearInfo.shortLabel} · {financialYearInfo.periodLabel}
                                </span>
                            ) : null}
                        </div>
                        <div className="flex items-center justify-between gap-2 rounded-lg bg-white/10 px-3 py-1.5 backdrop-blur sm:block sm:rounded-2xl sm:p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70 sm:text-xs">Achieved this year</p>
                            <p className="text-sm font-bold sm:mt-1 sm:text-2xl"><CountUp value={totals.achieved} format={formatAmount} duration={1200} /></p>
                            <p className="hidden text-xs text-white/70 sm:mt-1 sm:block">of {formatAmount(totals.target)} target</p>
                        </div>
                    </div>
                </Reveal>

                {canShareStudentForm() ? (
                    <a
                        href="/sales/student-form"
                        className="flex items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-white p-3 shadow-sm sm:hidden"
                    >
                        <span className="min-w-0">
                            <span className="block text-sm font-semibold text-slate-900">Student inquiry form</span>
                            <span className="block text-xs text-slate-500">Share the link and see who filled it</span>
                        </span>
                        <span className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white">Open</span>
                    </a>
                ) : null}

                <div className="grid grid-cols-2 gap-2 sm:gap-4 xl:grid-cols-4">
                    <StatCard
                        label="Total inquiries"
                        value={totalInquiries}
                        delay={80}
                        hint="All workflow states"
                        tone={{ icon: 'bg-gradient-to-br from-sky-500 to-blue-600', glow: 'bg-sky-500' }}
                        icon={ICONS.inquiries}
                    />
                    <StatCard
                        label="Follow-ups"
                        value={totalFollowups}
                        delay={160}
                        hint={`In ${currentMonth}`}
                        tone={{ icon: 'bg-gradient-to-br from-emerald-500 to-teal-600', glow: 'bg-emerald-500' }}
                        icon={ICONS.followups}
                    />
                    <StatCard
                        label="New inquiries"
                        value={totalNewInquiries}
                        delay={240}
                        hint={`In ${currentMonth}`}
                        tone={{ icon: 'bg-gradient-to-br from-violet-500 to-purple-600', glow: 'bg-violet-500' }}
                        icon={ICONS.fresh}
                    />
                    <StatCard
                        label="Target achieved"
                        value={overallRateRounded}
                        format={(n) => `${Math.round(n)}%`}
                        delay={320}
                        hint={`${formatAmount(pendingTotal)} pending`}
                        tone={{ icon: 'bg-gradient-to-br from-amber-500 to-orange-600', glow: 'bg-amber-500' }}
                        icon={ICONS.target}
                    />
                </div>

                <Card
                    delay={380}
                    title="Inquiry status summary"
                    subtitle="Track how many student inquiries sit in each workflow state."
                    aside={(
                        <div className="rounded-xl bg-slate-100 px-3 py-1.5 text-right sm:px-4 sm:py-2">
                            <p className="text-[11px] font-medium text-slate-500 sm:text-xs">Total</p>
                            <p className="text-lg font-bold text-slate-900 sm:text-xl">{totalInquiries}</p>
                        </div>
                    )}
                >
                    {statusSummary.length ? (
                        <>
                            <div className="mt-3 flex h-2.5 w-full sm:mt-5 sm:h-3 overflow-hidden rounded-full bg-slate-100">
                                {statusSummary.map((item, index) => (
                                    <Segment
                                        key={item.status}
                                        $delay={300 + index * 90}
                                        className={`${STATUS_STYLES[index % STATUS_STYLES.length].bar} transition-all duration-500`}
                                        style={{ width: `${totalInquiries > 0 ? (item.count / totalInquiries) * 100 : 0}%` }}
                                        title={`${item.status}: ${item.count}`}
                                    />
                                ))}
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:gap-3 lg:grid-cols-4">
                                {statusSummary.map((item, index) => {
                                    const style = STATUS_STYLES[index % STATUS_STYLES.length];
                                    const share = totalInquiries > 0 ? Math.round((item.count / totalInquiries) * 100) : 0;
                                    return (
                                        <Reveal key={item.status} $delay={350 + index * 80} className="rounded-xl border border-slate-200 bg-white p-2.5 transition hover:-translate-y-0.5 hover:shadow-md sm:p-4">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`inline-flex min-w-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-xs ${style.chip}`}>
                                                    <span className={`h-2 w-2 shrink-0 rounded-full ${style.bar}`} />
                                                    <span className="truncate">{item.status}</span>
                                                </span>
                                                <span className="text-xs font-medium text-slate-400">{share}%</span>
                                            </div>
                                            <p className="mt-1.5 text-lg font-bold text-slate-900 sm:mt-3 sm:text-2xl"><CountUp value={item.count} /></p>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <Empty>Provide inquiry data to see the distribution by status.</Empty>
                    )}
                </Card>

                <div className="grid gap-3 sm:gap-6 xl:grid-cols-2">
                    <Card
                        delay={460}
                        title="Daily follow-up activity"
                        subtitle={`Your daily follow-up momentum throughout ${currentMonth}.`}
                        aside={<span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">{totalFollowups} total</span>}
                    >
                        {followupData.length ? (
                            <DailyChart data={followupData} gradientId="followupGradient" from="#34d399" to="#0ea5e9" name="Follow-ups" />
                        ) : (
                            <Empty>No follow-up activity recorded this month yet.</Empty>
                        )}
                    </Card>

                    <Card
                        delay={540}
                        title="New inquiries added daily"
                        subtitle={`New student inquiries captured each day in ${currentMonth}.`}
                        aside={<span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">{totalNewInquiries} total</span>}
                    >
                        {inquiryData.length ? (
                            <DailyChart data={inquiryData} gradientId="inquiryGradient" from="#c084fc" to="#6366f1" name="New inquiries" />
                        ) : (
                            <Empty>No new inquiries recorded this month yet.</Empty>
                        )}
                    </Card>
                </div>

                <Card
                    delay={620}
                    title="Month-wise registrations"
                    subtitle="Actual registration revenue against the monthly targets set for the team."
                    aside={financialYearInfo ? (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                            {financialYearInfo.periodLabel}
                        </span>
                    ) : null}
                >
                    {chartData.length ? (
                        <div className="mt-3 space-y-3 sm:mt-6 sm:space-y-5">
                            <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-300" />Target</span>
                                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Achieved</span>
                            </div>
                            <div className="h-56 sm:h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 18, right: 4, left: isMobile ? -12 : 10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="achievedGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#34d399" />
                                                <stop offset="100%" stopColor="#059669" />
                                            </linearGradient>
                                            <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#e2e8f0" />
                                                <stop offset="100%" stopColor="#cbd5e1" />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: isMobile ? 10 : 12 }}
                                            interval={0}
                                            tickFormatter={(value) => (isMobile ? String(value).slice(0, 3) : value)}
                                        />
                                        <YAxis
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: isMobile ? 10 : 12 }}
                                            width={isMobile ? 36 : 60}
                                            tickFormatter={(value) =>
                                                new Intl.NumberFormat('en-US', {
                                                    notation: 'compact',
                                                    compactDisplay: 'short',
                                                }).format(value)
                                            }
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
                                            content={<ChartTooltip names={{ targetAmount: 'Target', achievedAmount: 'Achieved' }} />}
                                        />
                                        <Bar dataKey="targetAmount" fill="url(#targetGradient)" radius={[8, 8, 0, 0]} maxBarSize={28} />
                                        <Bar dataKey="achievedAmount" fill="url(#achievedGradient)" radius={[8, 8, 0, 0]} maxBarSize={28}>
                                            <LabelList
                                                dataKey="achievementRate"
                                                position="top"
                                                formatter={(value) => (value > 0 ? `${Math.round(value)}%` : '')}
                                                style={{ fontSize: isMobile ? '0.6rem' : '0.75rem', fontWeight: 600 }}
                                                fill="#0f172a"
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="grid grid-cols-[auto,1fr] items-center gap-2 sm:gap-4">
                                <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-2 sm:p-4">
                                    <AchievementRing rate={overallRateRounded} />
                                </div>
                                <div className="grid gap-2 sm:grid-cols-3 sm:gap-3">
                                    <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 sm:block sm:rounded-2xl sm:px-4 sm:py-4">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total target</p>
                                        <p className="text-sm font-bold sm:mt-2 sm:text-xl text-slate-900">{formatAmount(totals.target)}</p>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 sm:block sm:rounded-2xl sm:px-4 sm:py-4">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Total achieved</p>
                                        <p className="text-sm font-bold sm:mt-2 sm:text-xl text-emerald-900">{formatAmount(totals.achieved)}</p>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 sm:block sm:rounded-2xl sm:px-4 sm:py-4">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Pending amount</p>
                                        <p className="text-sm font-bold sm:mt-2 sm:text-xl text-amber-900">{formatAmount(pendingTotal)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Empty>No monthly registration data available yet.</Empty>
                    )}
                </Card>
            </div>
            )}
        </SalesLayout>
    );
}
